import { useEffect, useRef, useState } from 'react'

import { useQuery } from 'react-query'

import avatarPlaceholder from 'assets/images/user_placeholder.png'
import { IconTwoPeopleOutline } from 'assets/svg/sprite'
import AvatarHolder from 'components/UI/AvatarHolder/AvatarHolder'
import StackedAvatar from 'components/UI/StackedAvatar/StackedAvatar'
import ModalWrapper from 'features/ModalWrapper/ModalWrapper'
import {
  getFansGroups,
  getUsersFromFansGroup
} from 'services/endpoints/fanGroup'
import { useUserProfile } from 'store'

import { WhoAreYouWritingModal } from './WhoAreYouWritingToModal'

interface AudienceUser {
  id: number
  username?: string
  name?: string
  display_name?: string
  avatar?: { url?: string | null } | null
  [k: string]: any
}

interface GroupSelection {
  id: number
  name: string
  avatars?: string[]
  count?: number
  users?: AudienceUser[]
  members?: AudienceUser[]
  recipients?: AudienceUser[]
  total?: number
  [k: string]: any
}

type PreselectedAudience =
  | {
      kind: 'users'
      users: AudienceUser[]
      name: string
      avatars: string[]
      count: number
    }
  | {
      kind: 'group'
      group: GroupSelection
      name: string
      avatars: string[]
      count: number
    }
  | null

interface RecipientsProps {
  setAudience: (_val: any) => void
  preselectedAudience?: PreselectedAudience
  hasPreselectedAudience?: boolean
  avatarSize?: 'default' | 'small'
}

const getGroupCount = (group: GroupSelection): number => {
  if (typeof group.count === 'number' && group.count >= 0) return group.count
  if (Array.isArray(group.users)) return group.users.length
  if (Array.isArray(group.members)) return group.members.length
  if (Array.isArray(group.recipients)) return group.recipients.length
  if (typeof group.total === 'number') return group.total

  return 0
}

const getGroupAvatars = (group?: Pick<GroupSelection, 'avatars'> | null) => {
  return (group?.avatars ?? []).filter(Boolean)
}

export const Recipients = ({
  setAudience,
  preselectedAudience = null,
  hasPreselectedAudience = false,
  avatarSize = 'default'
}: RecipientsProps) => {
  const userData = useUserProfile()
  const [groupsModalOpen, setGroupsModalOpen] = useState(false)

  const [selectedGroup, setSelectedGroup] = useState({
    name: 'All',
    avatars: [] as string[],
    count: 0
  })

  const hasAppliedInitialSelectionRef = useRef(false)

  const isModel = userData?.role === 'model'
  const shouldSkipDefaultAudience = hasPreselectedAudience

  const { data: groups = [] } = useQuery(
    'fans-groups',
    async () => await getFansGroups(),
    {
      enabled: isModel && !shouldSkipDefaultAudience
    }
  )

  const allUsersId = groups?.[0]?.id ?? null

  const { data: allUsersData = [] } = useQuery(
    ['allUsersGroup', allUsersId],
    async () => await getUsersFromFansGroup(allUsersId, {}),
    {
      enabled: !!allUsersId && isModel && !shouldSkipDefaultAudience
    }
  )

  useEffect(() => {
    if (hasAppliedInitialSelectionRef.current) return

    if (preselectedAudience) {
      hasAppliedInitialSelectionRef.current = true

      setSelectedGroup({
        name: preselectedAudience.name,
        avatars: preselectedAudience.avatars.filter(Boolean),
        count: preselectedAudience.count ?? 0
      })

      setAudience(
        preselectedAudience.kind === 'users'
          ? preselectedAudience.users
          : preselectedAudience.group
      )

      return
    }

    if (shouldSkipDefaultAudience) return
    if (!groups.length) return

    const allGroup =
      groups.find((group: GroupSelection) => group.name === 'All') || groups[0]

    hasAppliedInitialSelectionRef.current = true

    setSelectedGroup({
      name: allGroup.name,
      avatars: getGroupAvatars(allGroup),
      count: getGroupCount(allGroup)
    })

    setAudience(allGroup)
  }, [preselectedAudience, groups, setAudience, shouldSkipDefaultAudience])

  const applySelection = (selection: any) => {
    if (selection.kind === 'group') {
      const group = selection.group

      setAudience(group)
      setSelectedGroup({
        name: group.name,
        avatars: getGroupAvatars(group),
        count: getGroupCount(group)
      })
    } else if (selection.kind === 'users') {
      const users = selection.users || []

      setAudience(users)
      setSelectedGroup({
        name: 'Selected Users',
        avatars: users
          .map((user: AudienceUser) => user?.avatar?.url)
          .filter(Boolean) as string[],
        count: users.length
      })
    } else {
      setAudience([])
      setSelectedGroup({
        name: 'All',
        avatars: [],
        count: 0
      })
    }

    setGroupsModalOpen(false)
  }

  return (
    <div className="newMessage__targets">
      <div className="newMessage__targets__avatar">
        <AvatarHolder
          img={userData?.avatar?.url ?? avatarPlaceholder}
          size="50"
          hasStory
          storyRead={false}
        />
      </div>

      <div
        className="newMessage__targets__groups"
        onClick={() => setGroupsModalOpen(true)}
      >
        <div className="newMessage__targets__groups__icon">
          <IconTwoPeopleOutline color="#ffffff" />
        </div>

        <div className="newMessage__targets__groups__text">
          {selectedGroup.name}
        </div>

        {selectedGroup.count > 0 && (
          <StackedAvatar
            avatars={selectedGroup.avatars}
            count={selectedGroup.count}
            userText
            avatarSize={avatarSize}
          />
        )}
      </div>

      <ModalWrapper
        open={groupsModalOpen}
        setOpen={setGroupsModalOpen}
        customClass="who-are-you-writing-to-modal-wrapper"
      >
        <WhoAreYouWritingModal
          closeFn={() => setGroupsModalOpen(false)}
          allUsersData={allUsersData}
          groups={groups}
          applyFn={applySelection}
        />
      </ModalWrapper>
    </div>
  )
}